import {
  isMitren,
  ALLOWED_SERVICES_SLUG,
  type TServiciosSlug,
} from '~~/constants';

export interface LandingServiceMeta {
  slug: TServiciosSlug;
  name: string;
  region: string;
  type: string;
  description: string;
  routeDescription: string;
  href: string;
  mapHref?: string;
}

const SERVICIOS_LANDING: Record<TServiciosSlug, LandingServiceMeta> = {
  biotren: {
    slug: 'biotren',
    name: 'Biotren',
    region: 'Gran Concepción',
    type: 'Urbano',
    description: 'Servicio urbano del Gran Concepción.',
    routeDescription:
      'Consulta horarios, estaciones y combinaciones del servicio Biotren.',
    href: '/biotren',
    mapHref: '/biotren/mapa',
  },
  'laja-talcahuano': {
    slug: 'laja-talcahuano',
    name: 'Corto Laja',
    region: 'Región del Biobío',
    type: 'Regional',
    description:
      'Servicio regional que conecta comunas entre Laja y Talcahuano.',
    routeDescription:
      'Revisa horarios, tarifas, estaciones y mapas del servicio Corto Laja.',
    href: '/laja-talcahuano',
  },
  'tren-araucania': {
    slug: 'tren-araucania',
    name: 'Tren Araucanía',
    region: 'Región de La Araucanía',
    type: 'Regional',
    description: 'Servicio Victoria – Temuco en la Región de La Araucanía.',
    routeDescription:
      'Consulta horarios, tarifas, estaciones y mapas del Tren Araucanía.',
    href: '/tren-araucania',
    mapHref: '/tren-araucania/mapa',
  },
  'rancagua-estacion-central': {
    slug: 'rancagua-estacion-central',
    name: 'Tren Rancagua – Estación Central',
    region: "Región Metropolitana y O'Higgins",
    type: 'Regional',
    description: 'Servicio entre Santiago y Rancagua con paradas intermedias.',
    routeDescription:
      'Revisa horarios, tarifas, estaciones y mapas del Tren Rancagua – Estación Central.',
    href: '/rancagua-estacion-central',
  },
  'tren-chillan': {
    slug: 'tren-chillan',
    name: 'Tren Chillán – Estación Central',
    region: 'Región del Ñuble y Metropolitana',
    type: 'Larga distancia',
    description: 'Servicio de larga distancia entre Santiago y Chillán.',
    routeDescription:
      'Consulta horarios, tarifas, estaciones y mapas del Tren Chillán – Estación Central.',
    href: '/tren-chillan',
  },
  'talca-constitucion': {
    slug: 'talca-constitucion',
    name: 'Talca – Constitución',
    region: 'Región del Maule',
    type: 'Regional',
    description: 'Servicio regional del Maule.',
    routeDescription:
      'Revisa horarios, tarifas, estaciones y mapas del servicio Talca – Constitución.',
    href: '/talca-constitucion',
  },
  'tren-san-fernando': {
    slug: 'tren-san-fernando',
    name: 'Tren San Fernando – Estación Central',
    region: "Región de O'Higgins y Metropolitana",
    type: 'Regional',
    description: 'Servicio entre Santiago y San Fernando.',
    routeDescription:
      'Consulta horarios, tarifas, estaciones y mapas del Tren San Fernando – Estación Central.',
    href: '/tren-san-fernando',
  },
  'llanquihue-puerto-montt': {
    slug: 'llanquihue-puerto-montt',
    name: 'Tren Llanquihue – Puerto Montt',
    region: 'Región de Los Lagos',
    type: 'Regional',
    description: 'Servicio regional en la Región de Los Lagos.',
    routeDescription:
      'Revisa horarios, tarifas, estaciones y mapas del Tren Llanquihue – Puerto Montt.',
    href: '/llanquihue-puerto-montt',
  },
} as const;

export function getLandingServices(): LandingServiceMeta[] {
  return ALLOWED_SERVICES_SLUG.map((slug) => SERVICIOS_LANDING[slug]);
}

export function getLandingServiceBySlug(
  slug: TServiciosSlug
): LandingServiceMeta | undefined {
  return SERVICIOS_LANDING[slug];
}

export const LANDING_COPY = {
  hero: {
    title: isMitren
      ? 'Horarios de trenes en Chile, simples y rápidos de consultar'
      : 'Horarios del Biotren, simples y rápidos de consultar',
    subtitle: isMitren
      ? 'Elige tu servicio principal para acceder más rápido a horarios, estaciones y recorridos frecuentes. Tu selección quedará guardada en este dispositivo.'
      : 'Elige tu servicio principal para acceder más rápido a horarios, estaciones y recorridos frecuentes. Tu selección quedará guardada en este dispositivo.',
    ctaPrimary: 'Seleccionar mi servicio',
    ctaSecondary: 'Ver todos los servicios',
    microcopy:
      'Mitren es una plataforma independiente para consultar información útil de servicios ferroviarios. No reemplaza los canales oficiales.',
  },
  selector: {
    title: 'Elige el servicio que usas con más frecuencia',
    subtitle:
      'Lo usaremos como acceso principal cada vez que entres a Mitren. Puedes cambiarlo después.',
    useService: 'Usar este servicio',
    viewSchedule: 'Ver horarios',
  },
  about: {
    title: isMitren ? '¿Qué es Mitren?' : '¿Qué es MiBiotren?',
    body: isMitren
      ? 'Mitren es una plataforma pensada para consultar horarios de trenes de forma rápida, simple y directa. Su objetivo es ayudarte a encontrar la información que necesitas sin recorrer varias páginas, especialmente cuando estás en movimiento o necesitas revisar un horario con poco tiempo.'
      : 'MiBiotren es una plataforma pensada para consultar horarios del Biotren de forma rápida, simple y directa. Su objetivo es ayudarte a encontrar la información que necesitas sin recorrer varias páginas, especialmente cuando estás en movimiento o necesitas revisar un horario con poco tiempo.',
    benefits: [
      {
        title: 'Consulta rápida',
        text: 'Accede a horarios y recorridos frecuentes sin pasos innecesarios.',
      },
      {
        title: 'Pensado para móvil',
        text: 'Interfaz simple para revisar información desde el teléfono, incluso en situaciones de apuro.',
      },
      {
        title: 'Preferencia guardada',
        text: 'Puedes elegir tu servicio principal para entrar más rápido la próxima vez.',
      },
      {
        title: 'Información centralizada',
        text: 'Reúne accesos a distintos servicios ferroviarios de Chile en un solo lugar.',
      },
    ],
  },
  supported: {
    title: 'Servicios disponibles en ' + (isMitren ? 'Mitren' : 'MiBiotren'),
    intro:
      'Actualmente puedes consultar información de distintos servicios ferroviarios urbanos, regionales e interurbanos en Chile.',
  },
  howItWorks: {
    title: '¿Cómo funciona?',
    steps: [
      'Elige el servicio que usas con más frecuencia.',
      isMitren
        ? 'Mitren'
        : 'Mibiotren' + ' guarda tu preferencia en este dispositivo.',
      'La próxima vez podrás entrar directamente a los horarios de tu servicio.',
      'Si necesitas revisar otro recorrido, puedes cambiar de servicio cuando quieras.',
    ],
  },
  disclaimer: {
    title: 'Información importante',
    body: `${isMitren ? 'Mitren.cl' : 'MiBiotren.cl'} es una herramienta independiente para facilitar la consulta de horarios y servicios ferroviarios. La información puede cambiar por operación, contingencias o ajustes del servicio. Para confirmaciones oficiales, revisa siempre los canales del operador correspondiente.`,
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: `¿${isMitren ? 'Mitren' : 'Mibiotren'} es una página oficial?`,
        answer: `No. ${isMitren ? 'Mitren' : 'Mibiotren'} es una plataforma independiente que busca facilitar la consulta de horarios y recorridos. Para información oficial, contingencias o cambios operacionales, revisa los canales del operador.`,
      },
      {
        question: '¿Puedo cambiar mi servicio principal?',
        answer:
          'Sí. Puedes cambiar tu servicio principal desde la selección de servicios o desde la configuración de la plataforma.',
      },
      {
        question: '¿La selección queda guardada?',
        answer:
          'Sí. La selección queda guardada en este dispositivo para que puedas entrar más rápido la próxima vez.',
      },
      {
        question: '¿Qué servicios están disponibles?',
        answer: isMitren
          ? 'Mitren incluye servicios como Biotren, Tren Laja – Talcahuano, Tren Araucanía, Tren Rancagua – Estación Central, Tren Chillán – Estación Central, Tren Talca – Constitución, Tren Llanquihue – Puerto Montt y Tren San Fernando – Estación Central.'
          : 'MiBiotren incluye servicios como Biotren, Corto Laja y Tren Araucanía.',
      },
      {
        question: '¿Los horarios pueden cambiar?',
        answer:
          'Sí. Los horarios pueden variar por operación, contingencias o cambios del servicio. Siempre es recomendable confirmar la información en los canales oficiales antes de viajar.',
      },
    ],
  },
  continueBanner: {
    label: 'Tu servicio principal es',
    cta: 'Continuar a mi servicio',
    change: 'Ver otros servicios',
  },
  footer: {
    brand: isMitren ? 'Mitren.cl' : 'MiBiotren.cl',
    tagline: isMitren
      ? 'Horarios de trenes de Chile, simples y rápidos de consultar.'
      : 'Horarios del Biotren, simples y rápidos de consultar.',
    disclaimer: `${isMitren ? 'Mitren.cl' : 'MiBiotren.cl'} es una plataforma independiente y no representa a EFE ni a otros operadores ferroviarios.`,
  },
} as const;
